import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import MicRecorder from "mic-recorder-to-mp3";
import React, { useContext, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { createUseStyles, useTheme } from "react-jss";
import { useIsFetching, useMutation } from "react-query";
import { useStopwatch } from "react-timer-hook";
import ArtinLogo from "../../assets/images/artin-logo.svg";
import { asrDecode, qnaStateTypes } from "../../core";
import { notifyError } from "../../core/utils/notify";
import ASRProcess from "../atoms/ASRProcess";
import { RecordButton } from "../atoms/RecordButton";
import { BotContext } from "../context";

const useStyles = createUseStyles((theme) => ({
  container: {
    width: "100%",
    background: "white",
    justifyContent: "center",
    zIndex: 1000,
    position: "absolute",
    bottom: 0,
    padding: 16,
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    position: "relative",
    borderBottomLeftRadius: "10px",
    borderBottomRightRadius: "10px",
  },
  input: {
    height: 48,
    padding: "12px 16px",
    border: "none!important",
    borderRadius: 20,
    direction: "rtl",
    boxShadow: "none!important",
    boxSizing: " border-box",
    fontSize: theme.messageFontSize,
    opacity: 1,
    outline: "none!important",
    marginInlineStart: 8,
    appearance: "none!important",
    flex: 1,
    background: "#F4F6F8",
    resize: "none",
    "&:disabled": {
      background: "#F4F6F8",
    },
    "&:focus": {
      background: "#F4F6F8",
    },
    "&.disabled:hover": {
      cursor: "not-allowed",
    },
    "&::placeholder": {
      color: "#9C9FA4",
    },
  },
  powerBy: {
    width: "calc(100% - 27px)",
    position: "absolute",
    zIndex: "1000",
    bottom: 4,
    textAlign: "center",
    fontSize: "10px!important",
    color: "#3A3F49",
    transform: "translateX(50%)",
    right: "50%",
    lineHeight: "24px",
  },
  artinLogo: {
    marginInline: 4,
    width: 21,
  },
  submitButton: {
    width: 48,
    height: 48,
    borderRadius: "100%",
    border: "none!important",
    background: theme.recordBtnBg,
    "&:hover": {
      cursor: "pointer",
    },
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& $svg": {
      width: 24,
      height: 24,
    },
  },
  recordButton: {
    width: 40,
    height: 40,
    backgroundColor: "unset",
    border: "none!important",
    borderRadius: "50%",
    flexBasis: (props) => {
      return props.isRecording ? 140 : 60;
    },
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#EFF0F1",
    },
    "&:disabled": {
      "& $svg": {
        color: "#B0B2B6",
      },
    },
    paddingLeft: 0,
    display: "flex",
    justifyContent: (props) => (props.isRecording ? "space-around" : "center"),
    alignItems: "center",
    position: "absolute",
    margin: 4,
  },
  "@media screen and (min-width: 688px) ": {
    container: {
      width: 688,
    },
    input: {
      height: 58,
      lineHeight: "34px",
    },
    recordButton: {
      width: 48,
      height: 48,
      left: 0,
      margin: "4px 8px",
    },
    submitButton: {
      width: 58,
      height: 58,
      "& $svg": {
        width: 30,
        height: 30,
      },
    },
    powerBy: {
      width: "650px",
      fontSize: "12px!important",
    },
    artinLogo: {
      width: 26,
    },
  },
}));

const Footer = () => {
  const [text, setText] = useState({
    question: "",
    label_question: "",
  });
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const isFetchingAskQ = useIsFetching(["askQuestion"]);
  const [Mp3Recorder] = useState(new MicRecorder({ bitRate: 128 }));

  const {
    globalProps,
    setButtonProps,
    askedQusetions,
    setAskedQuestions,
    inputRef,
    historyUser,
    rooms,
    chatStateMode,
    config,
  } = useContext(BotContext);
  const {
    seconds,
    minutes,
    start: startTimer,
    reset: resetTimer,
  } = useStopwatch({ autoStart: false });

  const { mutate: mutateAsrDecode, isLoading } = useMutation(
    async (file) => {
      const { data } = await asrDecode(file);
      return data;
    },
    {
      onSuccess({ data, status, message }) {
        if (status) {
          setText({ label_question: data?.result, question: data?.result });
        } else {
          notifyError(message);
        }
      },
      onError(error) {
        notifyError("خطا");
      },
    }
  );

  const [isRecording, setIsRecording] = useState(false);
  const [placeholder, setPlaceholder] = useState("");
  const theme = useTheme();
  const styles = useStyles({ theme, isRecording });
  const { isSaderatCustomization } = config;
  const placeHolderText = {
    botWelcome: isSaderatCustomization
      ? "صبا چطور میتونه کمکتون کنه . . . ؟"
      : "آرتین چطور میتونه کمکتون کنه . . . ؟",
    operatorWelcome: "گفت و گو با اپراتور، لطفا پیام خود را بنویسید. . .",
    operatorWaiting:
      "در انتظار پاسخگویی توسط اپراتور، لطفا پیام خود را بنویسید . . .",
    sending: "در حال ارسال پیام . . .",
  };
  useEffect(() => {
    if (isFetchingAskQ === 0) {
      !isMobile && inputRef.current.focus();
    }
  }, [isFetchingAskQ]);

  const submit = () => {
    if (chatStateMode === qnaStateTypes.BTC) {
      submitForBot();
    } else {
      submitForOperator();
    }
  };
  const submitForBot = () => {
    const q = {
      question: text.question.trim(),
      label_question: text.label_question.trim(),
    };
    const askedQusetionsClone = [...askedQusetions];
    askedQusetionsClone.splice(1, 0, q);
    setAskedQuestions(askedQusetionsClone);
    setButtonProps(q);
    globalProps.triggerNextStep({
      value: q,
      trigger: "5",
    });
    setText({
      question: "",
      label_question: "",
    });
    setPlaceholder(placeHolderText.sending);
  };
  const submitForOperator = () => {
    const q = {
      question: text.question.trim(),
      label_question: text.label_question.trim(),
    };
    setButtonProps(q);
    globalProps.triggerNextStep({
      value: q,
      trigger: "lvQ",
    });
    setText({
      question: "",
      label_question: "",
    });
    setPlaceholder(placeHolderText.operatorWelcome);
  };
  const handSubmitByEnter = (e) => {
    if (e.charCode === 13) {
      e.preventDefault();
      if (text.label_question.trim().length < 1) return;
      submit();
      !isMobile && inputRef.current.focus();
    }
  };

  const inputPlaceholder = () => {
    if (!isRecording && !isLoading) {
      setPlaceholder(
        chatStateMode === qnaStateTypes.BTC
          ? placeHolderText.botWelcome
          : chatStateMode === qnaStateTypes.LVC
          ? placeHolderText.operatorWelcome
          : chatStateMode === qnaStateTypes.WAT
          ? placeHolderText.operatorWaiting
          : ""
      );
    }
  };
  useEffect(() => {
    inputPlaceholder();
    if (!isMobile && !isRecording && !isLoading) {
      const end = inputRef.current.value.length;
      inputRef.current.setSelectionRange(end, end);
      inputRef.current.focus();
    }
  }, [isRecording, isLoading]);

  useEffect(() => {
    inputRef.current.blur();
  }, []);

  const handleKeyDown = (e) => {
    const suggestionsLenght = askedQusetions.length;
    if (suggestionsLenght > 1) {
      if (e.keyCode === 38) {
        e.preventDefault();
        if (suggestionIndex < suggestionsLenght - 1 || suggestionIndex === 0) {
          setSuggestionIndex(suggestionIndex + 1);
        }
      } else if (e.keyCode === 40) {
        e.preventDefault();
        if (suggestionIndex < suggestionsLenght && suggestionIndex !== 0) {
          setSuggestionIndex(suggestionIndex - 1);
        }
      }
    }
  };
  useEffect(() => {
    if (text.label_question?.length === 0) {
      setSuggestionIndex(0);
    }
  }, [text]);

  useEffect(() => {
    setText(
      {
        label_question: askedQusetions?.[suggestionIndex].label_question,
        question: askedQusetions?.[suggestionIndex].question,
      } ?? ""
    );
  }, [suggestionIndex]);
  useEffect(() => {
    if (isFetchingAskQ === 1) {
      setPlaceholder("در حال ارسال پیام . . . ");
    } else {
      setPlaceholder(
        chatStateMode === qnaStateTypes.BTC
          ? placeHolderText.botWelcome
          : chatStateMode === qnaStateTypes.LVC
          ? placeHolderText.operatorWelcome
          : chatStateMode === qnaStateTypes.WAT
          ? placeHolderText.operatorWaiting
          : ""
      );
    }
  }, [isFetchingAskQ, chatStateMode]);

  return (
    <div className={classNames(styles.container, "rsc-footer")}>
      {isRecording || isLoading ? (
        <ASRProcess
          {...{
            isLoading,
            mutateAsrDecode,
            setIsRecording,
            resetTimer,
            Mp3Recorder,
            seconds,
            minutes,
            setText,
          }}
        />
      ) : (
        <div className={styles.footer}>
          <Form.Control
            as="textarea"
            autoComplete="off"
            value={text.label_question}
            onChange={(e) =>
              setText({
                label_question: e.target.value,
                question: e.target.value,
              })
            }
            rows={1}
            className={styles.input}
            placeholder={placeholder}
            onKeyPress={handSubmitByEnter}
            onKeyDown={handleKeyDown}
            disabled={
              isRecording ||
              isLoading ||
              isFetchingAskQ === 1 ||
              !!historyUser ||
              rooms?.length === 0
            }
            maxLength={255}
            ref={inputRef}
          />
          <button
            onClick={submit}
            className={styles.submitButton}
            disabled={
              isLoading ||
              isRecording ||
              !!historyUser ||
              text.label_question.trim().length < 1
            }
          >
            <FontAwesomeIcon
              icon={faPaperPlane}
              color={theme.sendMessageBtnColor}
            />
          </button>
          {process.env.REACT_APP_ACCESS_ASR === "true" &&
            text.label_question.trim().length < 1 && (
              <RecordButton
                disabled={
                  isLoading || !!historyUser || !rooms || rooms.length === 0
                }
                className={styles.recordButton}
                {...{
                  setIsRecording,
                  startTimer,
                  resetTimer,
                  Mp3Recorder,
                }}
              />
            )}
        </div>
      )}
      <div className={styles.powerBy} id="powerBy">
        <img alt="logo" src={ArtinLogo} className={styles.artinLogo} />
        <span>قدرت گرفته از آرتین</span>
      </div>
    </div>
  );
};

export { Footer };
